import { useMemo, useState } from "react";
import {
  Activity, Terminal, ChevronDown, ChevronRight,
  Loader2, AlertCircle, CheckCircle2,
} from "lucide-react";
import {
  useCommandOutputs, useDevices,
  type CommandOutputRow, type CommandOutputsFilters,
} from "@/lib/queries";
import { cn } from "@/lib/utils";

function relativeTime(iso: string): string {
  const dt = (Date.now() - new Date(iso).getTime()) / 1000;
  if (dt < 60)        return "just now";
  if (dt < 3600)      return `${Math.floor(dt / 60)}m ago`;
  if (dt < 86_400)    return `${Math.floor(dt / 3600)}h ago`;
  return `${Math.floor(dt / 86_400)}d ago`;
}

function FeedRow({ r }: { r: CommandOutputRow }) {
  const [open, setOpen] = useState(false);
  const Chevron = open ? ChevronDown : ChevronRight;
  const KindIcon = r.connector_kind === "http_poll" ? Activity : Terminal;
  const truncated = r.output_full_len !== undefined && r.output_full_len > r.output.length;

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-accent/40"
      >
        <Chevron className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        {r.ok
          ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
          : <AlertCircle  className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-600 dark:text-red-400" />}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="text-sm font-medium">{r.device_name}</span>
            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-1.5 py-0 font-mono text-[10px] text-muted-foreground">
              <KindIcon className="h-2.5 w-2.5" />
              {r.connector_kind === "http_poll" ? "HTTP" : "Shell"}
            </span>
            <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
              {r.duration_ms} ms
            </span>
            <span className="ml-auto font-mono text-[11px] tabular-nums text-muted-foreground">
              {relativeTime(r.ran_at)}
            </span>
          </div>
          {!open && r.output && (
            <div className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground">
              {r.output.split("\n")[0]}
            </div>
          )}
          {!open && !r.output && r.error && (
            <div className="mt-0.5 truncate font-mono text-[11px] text-red-700 dark:text-red-400">
              {r.error}
            </div>
          )}
        </div>
      </button>
      {open && (
        <div className="space-y-2 border-t border-border bg-muted/30 px-3 py-2">
          {r.output && (
            <pre className="max-h-96 overflow-auto whitespace-pre-wrap font-mono text-[11px]">
              {r.output}
              {truncated && (
                <span className="text-muted-foreground"> … (full output is {r.output_full_len} bytes)</span>
              )}
            </pre>
          )}
          {r.error && (
            <pre className="max-h-32 overflow-auto whitespace-pre-wrap font-mono text-[11px] text-red-700 dark:text-red-400">
              {r.error}
            </pre>
          )}
          {!r.output && !r.error && (
            <span className="text-[11px] text-muted-foreground">(empty output)</span>
          )}
        </div>
      )}
    </div>
  );
}

export default function LiveData() {
  const devicesQ = useDevices();
  const [deviceId, setDeviceId] = useState<number | "">("");
  const [kind, setKind]         = useState<"" | "shell_command" | "http_poll">("");
  const [okFilter, setOkFilter] = useState<"" | "ok" | "failed">("");
  const [beforeId, setBeforeId] = useState<number | undefined>(undefined);

  const filters: CommandOutputsFilters = useMemo(() => {
    const f: CommandOutputsFilters = { limit: 50 };
    if (deviceId !== "") f.device_id = Number(deviceId);
    if (kind)            f.kind      = kind;
    if (okFilter)        f.ok        = okFilter === "ok";
    if (beforeId !== undefined) f.before_id = beforeId;
    return f;
  }, [deviceId, kind, okFilter, beforeId]);

  const q = useCommandOutputs(filters);
  const items = q.data?.items ?? [];

  const onLoadMore = () => {
    if (items.length === 0) return;
    setBeforeId(items[items.length - 1].id);
  };

  const onResetFilters = () => {
    setDeviceId(""); setKind(""); setOkFilter(""); setBeforeId(undefined);
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Live data</h1>
        <p className="text-sm text-muted-foreground">
          Output stream from every shell-command and HTTP-poll connector
          across your devices. Auto-refreshes every 30 s.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-3">
        <div>
          <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Device</label>
          <select
            value={deviceId}
            onChange={(e) => { setDeviceId(e.target.value ? Number(e.target.value) : ""); setBeforeId(undefined); }}
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
          >
            <option value="">All</option>
            {(devicesQ.data?.items ?? []).map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Kind</label>
          <select
            value={kind}
            onChange={(e) => { setKind(e.target.value as "" | "shell_command" | "http_poll"); setBeforeId(undefined); }}
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
          >
            <option value="">All</option>
            <option value="shell_command">Shell command</option>
            <option value="http_poll">HTTP poll</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Status</label>
          <select
            value={okFilter}
            onChange={(e) => { setOkFilter(e.target.value as "" | "ok" | "failed"); setBeforeId(undefined); }}
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
          >
            <option value="">All</option>
            <option value="ok">OK only</option>
            <option value="failed">Failed only</option>
          </select>
        </div>
        {(deviceId !== "" || kind !== "" || okFilter !== "" || beforeId !== undefined) && (
          <button
            type="button"
            onClick={onResetFilters}
            className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-accent"
          >
            Reset
          </button>
        )}
        <div className="ml-auto flex items-center gap-2 text-[11px] text-muted-foreground">
          {q.isFetching && <Loader2 className="h-3 w-3 animate-spin" />}
          {q.data && <span>{items.length} rows</span>}
        </div>
      </div>

      <div className={cn("rounded-lg border border-border bg-card", q.isError && "border-red-500/40")}>
        {q.isLoading && (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</div>
        )}
        {q.isError && (
          <div className="px-4 py-3 text-sm text-red-700 dark:text-red-400">
            {(q.error as Error)?.message ?? "Failed to load"}
          </div>
        )}
        {!q.isLoading && items.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            No output rows match these filters yet.
          </div>
        )}
        {items.map((r) => <FeedRow key={r.id} r={r} />)}
      </div>

      {items.length > 0 && (q.data?.limit ?? 0) <= items.length && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={onLoadMore}
            className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent"
          >
            Older →
          </button>
        </div>
      )}
    </div>
  );
}
